import type { Session } from '@orgus/protocol';
import { sessionAskOf } from '@orgus/protocol';
import {
  ArrowDown,
  ChevronDown,
  Bot,
  Brain,
  CircleCheck,
  FilePen,
  FileText,
  Globe,
  Info,
  MessageSquareText,
  Search,
  Sparkles,
  SquareTerminal,
  TriangleAlert,
  Workflow,
  Wrench,
} from '@orgus/ui/icons';
import { AnimatePresence, motion } from 'motion/react';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import type { ComponentType } from 'react';
import { Button, ErrorState, Markdown, Pressable, RowStatus, SkeletonText } from '@orgus/ui';
import { toUiError } from '../../lib/api.ts';
import type { UiError } from '../../lib/api.ts';
import { cn } from '@orgus/ui';
import { formatClock, plural } from '@orgus/ui';
import { useOrgus } from '../../lib/store.ts';
import { useLogStream } from '../../lib/useLogStream.ts';
import { demoTranscript } from './demoTranscript.ts';
import { speakerOf } from '../adapters/state.ts';
import { parseTranscript } from './transcript.ts';
import { streamNoteOf, streamNoteText } from './streamNote.ts';
import type { TranscriptEntry } from './transcript.ts';

const TOOL_ICON: Record<string, ComponentType<{ className?: string }>> = {
  Bash: SquareTerminal,
  Read: FileText,
  Edit: FilePen,
  Write: FilePen,
  NotebookEdit: FilePen,
  Grep: Search,
  Glob: Search,
  WebFetch: Globe,
  WebSearch: Globe,
  Agent: Bot,
  Task: Bot,
  Workflow: Workflow,
};

/**
 * What the session's own record says, for when no log stream is available. Never the title as
 * the person's prompt: a title may be the model's summary or Orgus's fallback ("Untitled session").
 */
function derivedEntries(s: Session): TranscriptEntry[] {
  const out: TranscriptEntry[] = [];
  for (const a of s.subagents) out.push({ key: `a-${a.id}`, kind: 'tool', at: a.startedAt, tool: 'Agent', text: a.description || a.type });
  for (const w of s.workflows) out.push({ key: `w-${w.id}`, kind: 'tool', at: w.startedAt, tool: 'Workflow', text: w.name });
  // The whole message of a session waiting on its question, else the one-line last text.
  const last = sessionAskOf(s.ext)?.text || s.lastText;
  if (last) out.push({ key: 'last', kind: 'say', at: s.lastActivity, text: last });
  return out.sort((a, b) => (a.at ?? 0) - (b.at ?? 0));
}

type Source = { entries: TranscriptEntry[]; state: 'loading' | 'live' | 'error' | 'summary' | 'kept'; error?: UiError; note?: string; retry?: () => void };

/** The log stream for one session (live hub), or the demo transcript (mock mode). */
function useTranscript(session: Session): Source {
  const mode = useOrgus((s) => s.mode);
  const { streamId, log, error: rawError, retry } = useLogStream({ type: 'session', id: session.id }, mode === 'live');
  const machine = useOrgus((s) => s.machines[session.machineId]);
  const note = streamNoteOf(rawError, machine ? machine.online : undefined);
  const error = useMemo(
    () =>
      rawError
        ? toUiError(rawError, {
            what: 'Could not open the transcript.',
            next: 'The summary below comes from the session record. Try again to stream the full transcript.',
          })
        : null,
    [rawError],
  );

  // The agent's own name marks its replies (Gemini CLI, OpenCode, ...), beside Claude and Codex.
  const speaker = speakerOf(session);
  const parsed = useMemo(() => (log?.text ? parseTranscript(log.text, 400, Date.now(), speaker) : []), [log?.text, speaker]);
  const demo = useMemo(() => (mode === 'mock' ? demoTranscript(session) : null), [mode, session]);
  const derived = useMemo(() => derivedEntries(session), [session]);

  if (demo) return { entries: demo, state: 'live' };
  // An offline machine or a route the server lacks is a calm note over what Orgus kept.
  if (rawError && note) return { entries: derived, state: 'kept', note: streamNoteText(note, machine?.name), retry };
  if (error) return { entries: derived, state: 'error', error, retry };
  if (!streamId) return { entries: [], state: 'loading' };
  if (!parsed.length) return { entries: derived, state: log ? 'summary' : 'loading' };
  return { entries: parsed, state: 'live' };
}

function ToolRun({ items }: { items: TranscriptEntry[] }) {
  const [open, setOpen] = useState(false);
  const LIMIT = 4;
  const hidden = items.length - LIMIT;
  const shown = open || hidden <= 1 ? items : items.slice(-LIMIT);
  return (
    <div className="overflow-hidden rounded-card border border-border-subtle bg-surface">
      {!open && hidden > 1 && (
        <Button variant="ghost" size="sm" className="w-full justify-start rounded-none text-fg-subtle" onClick={() => setOpen(true)}>
          Show {plural(hidden, 'earlier tool call')}
        </Button>
      )}
      <ul className="flex flex-col py-1">
        {shown.map((e) => {
          const Icon = (e.tool && TOOL_ICON[e.tool]) || Wrench;
          return (
            <li key={e.key} className="flex h-control-xs items-center gap-2 px-3">
              <Icon className="size-4 shrink-0 text-fg-subtle" aria-hidden />
              <span className="shrink-0 text-caption font-medium text-fg-muted">{e.tool}</span>
              <span className="min-w-0 flex-1 truncate font-mono text-caption text-fg-subtle">{e.text}</span>
              {e.at != null && <span className="shrink-0 text-caption text-fg-subtle num">{formatClock(e.at)}</span>}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

type Block =
  | { key: string; kind: 'one'; entry: TranscriptEntry }
  | { key: string; kind: 'tools'; items: TranscriptEntry[] }
  // Reasoning steps in a row (Gemini CLI sends each step apart): one entry, a paragraph per step.
  | { key: string; kind: 'thoughts'; items: TranscriptEntry[] };

export function toBlocks(entries: TranscriptEntry[]): Block[] {
  const out: Block[] = [];
  for (const e of entries) {
    const last = out[out.length - 1];
    if (e.kind === 'tool' && last?.kind === 'tools') last.items.push(e);
    else if (e.kind === 'tool') out.push({ key: e.key, kind: 'tools', items: [e] });
    else if (e.kind === 'thinking' && last?.kind === 'thoughts') last.items.push(e);
    else if (e.kind === 'thinking') out.push({ key: e.key, kind: 'thoughts', items: [e] });
    else out.push({ key: e.key, kind: 'one', entry: e });
  }
  return out;
}

const RAIL_ICON = {
  prompt: MessageSquareText,
  say: Sparkles,
  thinking: Brain,
  tool: Wrench,
  result: CircleCheck,
  error: TriangleAlert,
  // A line from Orgus (an approval answered, a mode set), never the agent's words.
  system: Info,
};

/** Who or what an entry is, above its text. */
function entryLabel(kind: TranscriptEntry['kind'], tools: number, speaker: string): ReactNode {
  switch (kind) {
    case 'prompt':
      return 'You';
    case 'tool':
      return plural(tools, 'tool call');
    case 'error':
      return 'Error';
    case 'result':
      return 'Turn ended';
    case 'thinking':
      return 'Reasoning';
    case 'system':
      return 'Session';
    default:
      return speaker;
  }
}

/** A message folds when it runs past this many lines (ADR 0056: whole, folded past a few lines). */
const FOLD_LINES = 8;

/**
 * Orgus's own lines carry Windows paths (Starting Claude Code in C:\Users\me\.app): Markdown reads
 * "\." as an escape and drops the backslash, so backslashes outside code spans are kept literal.
 */
export function keepBackslashes(text: string): string {
  return text
    .split(/(`[^`]*`)/)
    .map((part, i) => (i % 2 ? part : part.replace(/\\/g, '\\\\')))
    .join('');
}
/** A folded message shows this many lines, cut at a whole line. */
const SHOW_LINES = 6;
/** Reasoning folds sooner: its first step's subject and a line or two show, the rest opens on demand. */
const THINK_FOLD_LINES = 5;
const THINK_SHOW_LINES = 3;

/**
 * Where to cut a folded message so the last line shows whole: the bottom of the last line box
 * that ends within `limit` px of the top (paragraph and list gaps make a fixed height land
 * mid-glyph). Falls back to the limit when the text has no line boxes (an empty render, jsdom).
 */
function wholeLineCut(el: HTMLElement, limit: number): number {
  const top = el.getBoundingClientRect().top;
  const range = document.createRange();
  range.selectNodeContents(el);
  let cut = 0;
  for (const r of Array.from(range.getClientRects?.() ?? [])) {
    const bottom = r.bottom - top;
    if (r.height > 0 && bottom <= limit + 0.5 && bottom > cut) cut = bottom;
  }
  return Math.ceil(cut) || limit;
}

/**
 * What the agent said, or what you asked it: whole, Markdown rendered, folded to six whole
 * lines under a soft fade over the last one (never a cut through a line) with Show more when longer.
 */
function SayText({ text, className, quiet = false }: { text: string; className?: string; /** Reasoning: a tone quieter, bold included, and folded sooner. */ quiet?: boolean }) {
  const ref = useRef<HTMLDivElement>(null);
  const [cut, setCut] = useState<number | null>(null);
  const [open, setOpen] = useState(false);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const line = parseFloat(getComputedStyle(el).lineHeight) || 20;
    const fold = quiet ? THINK_FOLD_LINES : FOLD_LINES;
    const show = quiet ? THINK_SHOW_LINES : SHOW_LINES;
    setCut(el.scrollHeight > line * fold + 1 ? wholeLineCut(el, line * show) : null);
  }, [text, quiet]);
  const long = cut != null;
  return (
    <div className="flex flex-col items-start gap-1">
      {/* Reading text in a calm tone and regular weight; bold inside it is medium, never heavy. */}
      <div
        ref={ref}
        className={cn('w-full text-body [&_strong]:font-medium', quiet ? 'text-fg-subtle [&_strong]:text-fg-muted' : 'text-fg-muted [&_strong]:text-fg', long && !open && 'overflow-hidden mask-b-from-60%')}
        style={long && !open ? { maxHeight: cut } : undefined}
      >
        <Markdown source={text} className={className} />
      </div>
      {long && (
        <Pressable
          onClick={() => setOpen((o) => !o)}
          aria-expanded={open}
          className="flex items-center gap-1 rounded-control text-caption text-fg-subtle transition-colors duration-(--duration-fast) hover:text-fg-muted"
        >
          {open ? 'Show less' : 'Show more'}
          <ChevronDown className={cn('size-3 transition-transform duration-(--duration-fast)', open && 'rotate-180')} aria-hidden />
        </Pressable>
      )}
    </div>
  );
}

function Entry({ e }: { e: TranscriptEntry }) {
  if (e.kind === 'prompt') {
    return (
      <div className="rounded-card bg-hover px-3 py-2">
        <SayText text={e.text} className="text-fg-muted" />
      </div>
    );
  }
  if (e.kind === 'error') return <FoldText text={e.text} className="text-status-danger" />;
  if (e.kind === 'say') return <SayText text={e.text} className="text-fg-muted" />;
  // Reasoning reads a step quieter than the reply, and folds sooner.
  if (e.kind === 'thinking') return <SayText text={e.text} className="text-fg-subtle" quiet />;
  // A line from Orgus may carry Markdown too (a path in code, a bold name): rendered, quiet.
  if (e.kind === 'system') return <Markdown source={keepBackslashes(e.text)} className="text-fg-subtle" />;
  // Other lines read in fg-muted: a calm column of text, never a bright white wall.
  return <FoldText text={e.text} className="text-fg-muted" />;
}

/** Lines a plain entry shows before it folds (QA-57: a denied 40-line command filled the stream). */
const PLAIN_FOLD_LINES = 6;

function FoldText({ text, className }: { text: string; className: string }) {
  const [open, setOpen] = useState(false);
  const lines = text.split('\n');
  const long = lines.length > PLAIN_FOLD_LINES;
  return (
    <div className="flex flex-col items-start gap-1">
      <p className={cn('whitespace-pre-wrap break-words text-body', className)}>{long && !open ? `${lines.slice(0, PLAIN_FOLD_LINES).join('\n')}\n...` : text}</p>
      {long && (
        <Pressable onClick={() => setOpen((o) => !o)} aria-expanded={open} className="rounded-control text-caption text-fg-subtle hover:text-fg-muted">
          {open ? 'Show less' : `Show all ${plural(lines.length, 'line')}`}
        </Pressable>
      )}
    </div>
  );
}

/** Reasoning steps in a row: each its own paragraph, its bold subject above its body, folded as one. */
function Thoughts({ items }: { items: TranscriptEntry[] }) {
  const text = items.map((e) => e.text.trim()).filter(Boolean).join('\n\n');
  return <SayText text={text} className="text-fg-subtle" quiet />;
}

/**
 * The fill and bleed of the Jump to latest strip, per surface the timeline sits on: a solid strip
 * edge to edge, so the lines scrolling under it are hidden, never drawn through.
 */
const JUMP_STRIP = {
  popover: '-mx-6 bg-popover',
  card: '-mx-(--space-inset-card) bg-card',
} as const;

/** The session as a live timeline: prompts, what the agent said, tool calls (grouped), errors. */
export function Transcript({ session, surface = 'popover' }: { session: Session; /** What the timeline sits on: the drawer (popover) or a panel (card). */ surface?: keyof typeof JUMP_STRIP }) {
  const src = useTranscript(session);
  const blocks = useMemo(() => toBlocks(src.entries), [src.entries]);
  const endRef = useRef<HTMLDivElement>(null);
  const startRef = useRef<HTMLDivElement>(null);
  const [endVisible, setEndVisible] = useState(true);
  // Whether the timeline has started well above the bottom edge: until it has, the strip would
  // only cover its first lines, so it waits.
  const [started, setStarted] = useState(false);
  useEffect(() => {
    const end = endRef.current;
    const start = startRef.current;
    if (!end || !start) return;
    const io = new IntersectionObserver(([entry]) => setEndVisible(!!entry?.isIntersecting));
    io.observe(end);
    const so = new IntersectionObserver(
      ([entry]) => entry && setStarted(entry.boundingClientRect.top < (entry.rootBounds?.bottom ?? window.innerHeight)),
      { rootMargin: '0px 0px -240px 0px' },
    );
    so.observe(start);
    return () => {
      io.disconnect();
      so.disconnect();
    };
  }, [src.state]);

  if (src.state === 'loading') {
    return (
      <div role="status" aria-label="Loading the transcript" className="flex flex-col gap-4 py-2">
        <SkeletonText lines={2} />
        <SkeletonText lines={3} />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <div ref={startRef} aria-hidden className="-mb-3 h-px" />
      {src.state === 'error' && src.error && <ErrorState variant="inline" error={src.error} onRetry={src.retry} />}
      {src.state === 'kept' && src.note && <p className="text-caption text-fg-subtle">{src.note}</p>}
      {src.state === 'summary' && (
        <p className="text-caption text-fg-subtle">No readable entries yet; this summary comes from the session record.</p>
      )}
      <ol className="relative flex flex-col gap-3">
        <span aria-hidden className="absolute bottom-2 left-3 top-2 w-px bg-border-subtle" />
        <AnimatePresence initial={false}>
          {blocks.map((b) => {
            const kind = b.kind === 'tools' ? 'tool' : b.kind === 'thoughts' ? 'thinking' : b.entry.kind;
            const Icon = RAIL_ICON[kind];
            const at = b.kind === 'one' ? b.entry.at : b.items[0]?.at;
            return (
              <motion.li
                key={b.key}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
                className="relative flex gap-3"
              >
                <span
                  className={cn(
                    'relative -ml-px flex size-6 shrink-0 items-center justify-center rounded-pill border border-border bg-raised [&_svg]:size-3',
                    kind === 'prompt' && 'text-accent-text',
                    kind === 'say' && 'text-fg-muted',
                    kind === 'thinking' && 'text-fg-subtle',
                    kind === 'tool' && 'text-fg-subtle',
                    kind === 'error' && 'border-status-danger-line text-status-danger',
                    kind === 'result' && 'text-fg-muted',
                    kind === 'system' && 'text-fg-subtle',
                  )}
                  aria-hidden
                >
                  <Icon />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="mb-1 flex items-center gap-2 text-caption text-fg-subtle">
                    <span className="font-medium">{entryLabel(kind, b.kind === 'tools' ? b.items.length : 1, speakerOf(session))}</span>
                    {at != null && <span className="num">{formatClock(at)}</span>}
                  </p>
                  {b.kind === 'tools' ? <ToolRun items={b.items} /> : b.kind === 'thoughts' ? <Thoughts items={b.items} /> : <Entry e={b.entry} />}
                </div>
              </motion.li>
            );
          })}
        </AnimatePresence>
        {session.status === 'working' && (
          <li className="relative flex h-6 items-center pl-1" aria-live="polite">
            <RowStatus tone="working" icon="working" label="Working" />
          </li>
        )}
      </ol>
      <div ref={endRef} aria-hidden className="h-px" />
      <AnimatePresence>
        {!endVisible && started && blocks.length > 3 && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
            // A reserved strip of its own at the bottom edge, solid and ruled like a footer: the
            // timeline scrolls under it and is hidden there, so the button never sits on a line.
            className={cn('sticky bottom-0 z-(--z-sticky) flex justify-center border-t border-border-subtle py-2', JUMP_STRIP[surface])}
          >
            <Button
              size="sm"
              icon={<ArrowDown />}
              variant="ghost"
              className="text-fg-muted"
              onClick={() => endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' })}
            >
              Jump to latest
            </Button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
